import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

interface AIConsentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConsentGranted?: () => void;
}

const AIConsentModal: React.FC<AIConsentModalProps> = ({ isOpen, onClose, onConsentGranted }) => {
    const { userProfile, updateSettings } = useAuth();
    const [isChecked, setIsChecked] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsChecked(userProfile?.aiConsentEnabled || false);
        }
    }, [isOpen, userProfile]);

    if (!isOpen) return null;

    const handleAgree = async () => {
        if (!isChecked) return;
        setIsSaving(true);
        try {
            await updateSettings({ aiConsentEnabled: true });
            if (onConsentGranted) {
                onConsentGranted();
            }
            onClose();
        } catch (error) {
            console.error("Failed to save AI consent:", error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-[80] flex items-center justify-center p-4 overflow-y-auto animate-fade-in" onClick={onClose}>
            <div 
                className="bg-gradient-to-br from-[#1a0537] to-[#3b0764] rounded-2xl shadow-2xl w-full max-w-lg flex flex-col animate-modal-pop-in origin-center text-white border border-purple-500/30 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                id="ai-consent-modal-container"
            >
                {/* Header Graphic / Theme banner */}
                <div className="bg-gradient-to-r from-purple-800 to-[var(--accent-pink)] py-6 px-6 relative text-center">
                    <div className="absolute top-4 right-4">
                        <button 
                            id="ai-consent-close-btn"
                            onClick={onClose} 
                            className="text-white/60 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
                            aria-label="Close"
                        >
                            <i className="ph-duotone ph-x text-xl"></i>
                        </button>
                    </div>
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 border border-white/20 mb-3">
                        <i className="ph-duotone ph-brain text-3xl text-yellow-300"></i>
                    </div>
                    <h2 className="text-xl md:text-2xl font-orbitron font-extrabold uppercase tracking-widest text-white">
                        AI Creator Studio
                    </h2>
                    <p className="text-xs text-purple-100 font-semibold tracking-wider uppercase mt-1">
                        Data Privacy & Consent Gate
                    </p>
                </div>

                <div className="p-6 md:p-8 space-y-5 leading-relaxed max-h-[60vh] overflow-y-auto custom-scrollbar text-sm text-gray-200">
                    <p className="text-gray-300">
                        To power the game's interactive AI Image Generator, custom Clue Generator, smart In-Game Hints, and virtual Support Chatbot, our application securely integrates with the high-performance <span className="font-bold text-white">Google Gemini API</span>.
                    </p>

                    <div className="space-y-4 border-l-2 border-[var(--accent-pink)] pl-4 py-1">
                        <div>
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <i className="ph-duotone ph-database text-purple-400"></i>
                                What Data is Shared?
                            </h3>
                            <p className="text-gray-300 text-xs mt-1">
                                Only your written game description, designated answer word, clue cards, or direct chatbot support query transcript is securely processed. No contacts, photos, or other personal identifiers are shared.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <i className="ph-duotone ph-shield-check text-purple-400"></i>
                                Who is the Recipient?
                            </h3>
                            <p className="text-gray-300 text-xs mt-1">
                                Data is processed strictly by Google's secure enterprise service. In alignment with privacy regulations, inputs are not retained or utilized to train public AI models.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <i className="ph-duotone ph-user-gear text-purple-400"></i>
                                Control & Opt-Out
                            </h3>
                            <p className="text-gray-300 text-xs mt-1">
                                You hold absolute control. Consent is optional, and you can freely toggle, review, or withdraw your decision at any moment through the app's Account Settings page.
                            </p>
                        </div>
                    </div>

                    <p className="text-xs text-gray-400 italic">
                        Please review our full Privacy Policy under settings for complete coverage of security and user safety standards.
                    </p>
                </div>

                {/* Consent Action Area */}
                <div className="p-6 bg-black/30 border-t border-purple-500/20 space-y-4">
                    <div className="flex items-start gap-3">
                        <input 
                            type="checkbox" 
                            id="ai-consent-checkbox" 
                            checked={isChecked}
                            onChange={(e) => setIsChecked(e.target.checked)}
                            className="w-5 h-5 rounded border-purple-400 text-[var(--accent-pink)] bg-purple-950 focus:ring-0 focus:ring-offset-0 mt-0.5 cursor-pointer accent-pink-500"
                        />
                        <label htmlFor="ai-consent-checkbox" className="text-xs text-gray-300 cursor-pointer select-none leading-normal font-semibold">
                            I understand and give permission to transmit required gameplay details to Google Gemini API to enable AI features.
                        </label>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <button 
                            id="ai-consent-decline-btn"
                            onClick={onClose}
                            className="flex-1 bg-white/5 hover:bg-white/10 active:scale-95 text-gray-300 font-semibold py-3 px-6 rounded-xl text-sm transition-all text-center border border-white/10"
                        >
                            Cancel
                        </button>
                        <button 
                            id="ai-consent-agree-btn"
                            onClick={handleAgree}
                            disabled={!isChecked || isSaving}
                            className={`flex-1 bg-gradient-to-r from-[var(--accent-pink)] to-purple-600 text-white font-orbitron font-bold py-3 px-6 rounded-xl text-sm uppercase tracking-wider transition-all shadow-[0_4px_15px_rgba(236,72,153,0.3)] text-center flex items-center justify-center gap-2 ${
                                (!isChecked || isSaving) ? 'opacity-50 cursor-not-allowed shadow-none' : 'hover:opacity-95 active:scale-95'
                            }`}
                        >
                            {isSaving ? (
                                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            ) : (
                                <>
                                    <span>Agree & Enable</span>
                                    <i className="ph-bold ph-arrow-right"></i>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIConsentModal;
